const STORAGE_FLAG = "CHAT_SOCKET_DEBUG";
const STORAGE_LOGS = "CHAT_SOCKET_DEBUG_LOGS";
const MAX_LOGS = 200;
const MAX_STRING = 4000;
const LISTENERS_KEY = "__chatSocketDebugAttached";

const listeners = new Set();
let memoryLogs = null;

const safeJsonParse = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const readFlagFromUrl = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("chatDebug");
    if (value === "1" || value === "true") return true;
    if (value === "0" || value === "false") return false;
  } catch {
    /* ignore */
  }
  return null;
};

export const syncChatSocketDebugFromUrl = () => {
  const fromUrl = readFlagFromUrl();
  if (fromUrl == null) return isChatSocketDebugEnabled();
  try {
    localStorage.setItem(STORAGE_FLAG, fromUrl ? "1" : "0");
  } catch {
    /* ignore */
  }
  return fromUrl;
};

export const isChatSocketDebugEnabled = () => {
  if (typeof window === "undefined") return false;
  const fromUrl = readFlagFromUrl();
  if (fromUrl != null) return fromUrl;
  try {
    return localStorage.getItem(STORAGE_FLAG) === "1";
  } catch {
    return false;
  }
};

export const setChatSocketDebugEnabled = (enabled) => {
  try {
    localStorage.setItem(STORAGE_FLAG, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
  notifyListeners();
};

const loadLogs = () => {
  if (memoryLogs) return memoryLogs;
  try {
    const parsed = safeJsonParse(localStorage.getItem(STORAGE_LOGS) || "[]", []);
    memoryLogs = Array.isArray(parsed) ? parsed : [];
  } catch {
    memoryLogs = [];
  }
  return memoryLogs;
};

const persistLogs = () => {
  if (!memoryLogs) return;
  try {
    localStorage.setItem(STORAGE_LOGS, JSON.stringify(memoryLogs));
  } catch {
    // Quota exceeded — drop older half and retry once
    memoryLogs = memoryLogs.slice(-Math.floor(MAX_LOGS / 2));
    try {
      localStorage.setItem(STORAGE_LOGS, JSON.stringify(memoryLogs));
    } catch {
      /* ignore */
    }
  }
};

const notifyListeners = () => {
  const snapshot = getChatSocketLogs();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
};

export const subscribeChatSocketDebug = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const sanitizeValue = (value, depth = 0) => {
  if (value == null) return value;
  if (depth > 4) return "[MaxDepth]";

  if (typeof value === "string") {
    if (value.length > MAX_STRING) {
      return `${value.slice(0, MAX_STRING)}…[truncated ${value.length} chars]`;
    }
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") return value;

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return `[Blob type=${value.type || "unknown"} size=${value.size}]`;
  }

  if (typeof File !== "undefined" && value instanceof File) {
    return `[File name=${value.name} type=${value.type || "unknown"} size=${value.size}]`;
  }

  if (Array.isArray(value)) {
    if (value.length > 50) {
      return [
        ...value.slice(0, 50).map((item) => sanitizeValue(item, depth + 1)),
        `[…${value.length - 50} more]`,
      ];
    }
    return value.map((item) => sanitizeValue(item, depth + 1));
  }

  if (typeof value === "object") {
    if (typeof value.message === "string" && value.name) {
      // Error-like
      return {
        name: value.name,
        message: value.message,
        ...(value.description ? { description: value.description } : {}),
      };
    }

    const out = {};
    Object.keys(value).forEach((key) => {
      // Skip huge / sensitive-ish internal fields if ever present
      if (key === "localPreview" && typeof value[key] === "string" && value[key].startsWith("blob:")) {
        out[key] = "[blob:url]";
        return;
      }
      try {
        out[key] = sanitizeValue(value[key], depth + 1);
      } catch {
        out[key] = "[Unserializable]";
      }
    });
    return out;
  }

  return String(value);
};

export const logChatSocket = ({
  direction = "sys",
  event,
  payload,
  meta,
} = {}) => {
  if (!isChatSocketDebugEnabled()) return;

  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ts: new Date().toISOString(),
    direction, // in | out | sys
    event: event || "unknown",
    payload: sanitizeValue(payload),
    meta: meta ? sanitizeValue(meta) : undefined,
  };

  const logs = loadLogs();
  logs.push(entry);
  while (logs.length > MAX_LOGS) logs.shift();
  memoryLogs = logs;
  persistLogs();
  notifyListeners();
};

export const getChatSocketLogs = () => [...loadLogs()];

export const clearChatSocketLogs = () => {
  memoryLogs = [];
  try {
    localStorage.removeItem(STORAGE_LOGS);
  } catch {
    /* ignore */
  }
  notifyListeners();
};

export const exportChatSocketLogs = () => {
  const enabled = isChatSocketDebugEnabled();
  const logs = getChatSocketLogs();
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      debugEnabled: enabled,
      count: logs.length,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      href: typeof window !== "undefined" ? window.location.href : null,
      logs,
    },
    null,
    2,
  );
};

/**
 * Attach once per socket instance: lifecycle + onAny inbound + emit wrap for outbound.
 */
export const attachChatSocketDebug = (socket, context = {}) => {
  if (!socket || socket[LISTENERS_KEY]) return socket;
  socket[LISTENERS_KEY] = true;

  const logCtx = () => ({
    ...context,
    socketId: socket.id || null,
    connected: Boolean(socket.connected),
    transport: socket.io?.engine?.transport?.name || null,
  });

  logChatSocket({
    direction: "sys",
    event: "socket_created",
    payload: logCtx(),
  });

  socket.on("connect", () => {
    logChatSocket({
      direction: "sys",
      event: "connect",
      payload: logCtx(),
    });
  });

  socket.on("disconnect", (reason) => {
    logChatSocket({
      direction: "sys",
      event: "disconnect",
      payload: { reason, ...logCtx() },
    });
  });

  socket.on("connect_error", (err) => {
    logChatSocket({
      direction: "sys",
      event: "connect_error",
      payload: {
        message: err?.message || String(err),
        description: err?.description,
        ...logCtx(),
      },
    });
  });

  socket.on("connectionError", (payload) => {
    logChatSocket({
      direction: "sys",
      event: "connectionError",
      payload,
      meta: logCtx(),
    });
  });

  socket.on("exception", (payload) => {
    logChatSocket({
      direction: "sys",
      event: "exception",
      payload,
      meta: logCtx(),
    });
  });

  socket.io?.on?.("reconnect_attempt", (attempt) => {
    logChatSocket({
      direction: "sys",
      event: "reconnect_attempt",
      payload: { attempt, ...logCtx() },
    });
  });

  socket.io?.on?.("reconnect", (attempt) => {
    logChatSocket({
      direction: "sys",
      event: "reconnect",
      payload: { attempt, ...logCtx() },
    });
  });

  if (typeof socket.onAny === "function") {
    socket.onAny((event, ...args) => {
      // Skip noise that is already covered as sys
      if (
        event === "connect" ||
        event === "disconnect" ||
        event === "connect_error" ||
        event === "connectionError" ||
        event === "exception"
      ) {
        return;
      }
      logChatSocket({
        direction: "in",
        event,
        payload: args.length <= 1 ? args[0] : args,
      });
    });
  }

  const originalEmit = socket.emit.bind(socket);
  socket.emit = (event, ...args) => {
    // Last arg may be an ack callback — don't put functions in the log
    const payloadArgs = args.filter((arg) => typeof arg !== "function");
    logChatSocket({
      direction: "out",
      event: String(event),
      payload: payloadArgs.length <= 1 ? payloadArgs[0] : payloadArgs,
      meta: {
        connected: Boolean(socket.connected),
        hasAck: args.some((arg) => typeof arg === "function"),
      },
    });
    return originalEmit(event, ...args);
  };

  return socket;
};
